import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom';
import { useEntitiesByQuery } from 'hamster-react';
import { Offer } from 'cityads-v3/types/offers'
import { MicrosoftAdsIntegration, MicrosoftAdsAccount } from 'cityads-v3/types/microsoftAds';
import { useCheckPermission } from 'cityads-v3/hooks/permissions';

import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip'
import WaitingButton from 'cityads-v3/components/Buttons/WaitingButton';

type IntegrationButtonProps = {
  offerId: Offer['id']
}

const IntegrationButton: React.FC<IntegrationButtonProps> = function ({ offerId }) {
  const { t } = useTranslation(['Offers'])

  const checkPermission = useCheckPermission();
  const hasParkingPermission = checkPermission('show_microsoft_ads_integrations');

  const integrationsQuery = useMemo(() => offerId ? { offer_ids: offerId } : null, [offerId]);
  const [integrationsLoading, , integrations] = useEntitiesByQuery('microsoftAdsIntegration', integrationsQuery) as [boolean, boolean, MicrosoftAdsIntegration[] | null];

  const adsAccountsQuery = useMemo(
    () => integrations?.length
      ? { ids: integrations.map(integration => integration.microsoft_ads_account_id) }
      : null,
    [integrations]
  );
  const [adsAccountsLoading, , adsAccounts] = useEntitiesByQuery('microsoftAdsAccount', adsAccountsQuery) as [boolean, boolean, MicrosoftAdsAccount[] | null];

  if (!hasParkingPermission) {
    return null;
  }

  return (
    <WaitingButton
      component={Link}
      startIcon={integrations?.length ? <CheckOutlinedIcon /> : null}
      endIcon={<Tooltip title={integrations?.length ? t('exports.integrated') : t('exports.notIntegrated')}><InfoOutlinedIcon /></Tooltip>}
      to={integrations?.length ?
        `/publisher/tools/microsoftAdsIntegration?integration=${integrations[0].id}&account=${adsAccounts?.[0]?.microsoft_account_id}` :
        `/publisher/tools/microsoftAdsIntegration/create?offerIds=${offerId}`
      }
      waiting={integrationsLoading || adsAccountsLoading}
    >
      {t('exports.microsoftAdsIntegration')}
    </WaitingButton>
  )
}

export default IntegrationButton;
